import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { getTermsUrl } from '@shared/navigation';

/**
 * MasterTermsPage - Acuerdo Maestro Unificado del Ecosistema TheFomLabs
 * 
 * Documento legal centralizado que incluye:
 * - Acuerdo Maestro (términos generales del ecosistema)
 * - Secciones específicas por producto (Houseme, ThePlatform360, Traela, Sonmany)
 * - Integración de los 50 puntos de blindaje para ThePlatform360
 * 
 * Versión: v1.0 (Edición Ecosistema Unificado)
 * Fecha de Vigencia: 1 de febrero de 2026
 */
const MasterTermsPage: React.FC = () => {
  const navigate = useNavigate();

  // Habilitar scroll en body y html cuando se monta la página
  useEffect(() => {
    const originalBodyOverflow = document.body.style.overflow;
    const originalHtmlOverflow = document.documentElement.style.overflow;
    const originalRootOverflow = document.getElementById('root')?.style.overflow || '';

    // Habilitar scroll
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
    const rootElement = document.getElementById('root');
    if (rootElement) {
      rootElement.style.overflow = 'auto';
    }

    // Restaurar estilos originales al desmontar
    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.documentElement.style.overflow = originalHtmlOverflow;
      if (rootElement) {
        rootElement.style.overflow = originalRootOverflow;
      }
    };
  }, []);

  // Función para scroll suave a secciones
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div 
      className="w-full bg-white"
      style={{ 
        height: '100vh',
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch'
      }}
    >
      {/* Header con botón de navegación */}
      <div className="border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="container mx-auto max-w-4xl px-4 py-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors duration-200 text-sm font-medium"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Volver al inicio</span>
          </button>
        </div>
      </div>

      {/* Contenido principal */}
      <main className="container mx-auto max-w-4xl px-4 py-8 md:py-12">
        <article className="prose prose-lg prose-gray max-w-none">
          {/* Título principal */}
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            ACUERDO MAESTRO DEL ECOSISTEMA THEFOMLABS
          </h1>
          <p className="text-xl text-gray-600 mb-4">
            Términos y Condiciones Unificados - Ecosistema Completo
          </p>

          {/* Metadatos */}
          <div className="text-sm text-gray-600 mb-8 space-y-1">
            <p><strong>Versión:</strong> v1.0 (Edición Ecosistema Unificado)</p>
            <p><strong>Fecha de Vigencia:</strong> 1 de febrero de 2026</p>
            <p><strong>Entidad Responsable:</strong> Grupo FOM, S.R.L.</p>
            <p><strong>RNC:</strong> 133308355</p>
            <p><strong>Domicilio Legal:</strong> B Esquina 1ra No. 1, Santo Domingo, República Dominicana</p>
            <p><strong>Email Legal:</strong> legal@thefomlabs.com</p>
            <p><strong>DPO:</strong> dpo@thefomlabs.com</p>
            <p><strong>Ámbito:</strong> Ecosistema TheFomLabs (Houseme, ThePlatform360, Traela, Sonmany)</p>
          </div>

          {/* Tabla de Contenido Completa */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Tabla de Contenido</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <nav className="space-y-2">
                <p className="font-semibold text-gray-900 text-sm mb-2">ACUERDO MAESTRO</p>
                <button onClick={() => scrollToSection('preambulo')} className="block text-blue-600 hover:text-blue-800 hover:underline text-sm text-left">
                  Preámbulo
                </button>
                <button onClick={() => scrollToSection('cuenta-unificada')} className="block text-blue-600 hover:text-blue-800 hover:underline text-sm text-left">
                  Cuenta Unificada y SSO
                </button>
                <button onClick={() => scrollToSection('uso-datos')} className="block text-blue-600 hover:text-blue-800 hover:underline text-sm text-left">
                  Uso de Datos y Monetización
                </button>
                <button onClick={() => scrollToSection('ia-propiedad')} className="block text-blue-600 hover:text-blue-800 hover:underline text-sm text-left">
                  IA y Propiedad Intelectual
                </button>
                <button onClick={() => scrollToSection('seguridad')} className="block text-blue-600 hover:text-blue-800 hover:underline text-sm text-left">
                  Seguridad y Terceros
                </button>
                <button onClick={() => scrollToSection('retencion')} className="block text-blue-600 hover:text-blue-800 hover:underline text-sm text-left">
                  Retención y Aspectos Fiscales
                </button>
              </nav>
              <nav className="space-y-2">
                <p className="font-semibold text-gray-900 text-sm mb-2">PRODUCTOS ESPECÍFICOS</p>
                <button onClick={() => scrollToSection('houseme')} className="block text-blue-600 hover:text-blue-800 hover:underline text-sm text-left">
                  Houseme
                </button>
                <button onClick={() => scrollToSection('theplatform360')} className="block text-blue-600 hover:text-blue-800 hover:underline text-sm text-left">
                  ThePlatform360 (Empresa)
                </button>
                <button onClick={() => scrollToSection('traela')} className="block text-blue-600 hover:text-blue-800 hover:underline text-sm text-left">
                  Traela
                </button>
                <button onClick={() => scrollToSection('sonmany')} className="block text-blue-600 hover:text-blue-800 hover:underline text-sm text-left">
                  Sonmany
                </button>
                <p className="font-semibold text-gray-900 text-sm mb-2 mt-4">DISPOSICIONES FINALES</p>
                <button onClick={() => scrollToSection('jurisdiccion')} className="block text-blue-600 hover:text-blue-800 hover:underline text-sm text-left">
                  Jurisdicción y Arbitraje
                </button>
                <button onClick={() => scrollToSection('contacto')} className="block text-blue-600 hover:text-blue-800 hover:underline text-sm text-left">
                  Contacto
                </button>
              </nav>
            </div>
          </div>

          {/* ===== PREÁMBULO ===== */}
          <section id="preambulo" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              PREÁMBULO
            </h2>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-4">
              <p className="text-gray-700 leading-relaxed mb-2">
                <strong>Grupo FOM, S.R.L.</strong> (en adelante, "Nosotros", "TheFomLabs" o "Grupo FOM") 
                opera un ecosistema integrado de aplicaciones y servicios digitales que incluye, 
                entre otros, <strong>Houseme</strong>, <strong>ThePlatform360</strong>, <strong>Traela</strong> 
                y <strong>Sonmany</strong>.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Este <strong>Acuerdo Maestro</strong> establece los términos y condiciones generales 
                que rigen el uso de todas las aplicaciones del ecosistema. Cada aplicación puede tener 
                términos adicionales específicos que complementan (no reemplazan) este acuerdo maestro.
              </p>
            </div>
            <p className="text-gray-700 leading-relaxed mb-4">
              Al registrarse, crear una cuenta o utilizar cualquier aplicación del ecosistema 
              TheFomLabs, usted confirma que ha leído, entendido y acepta estar legalmente vinculado 
              por este Acuerdo Maestro y los términos específicos de cada aplicación que utilice.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Si no está de acuerdo con estos términos, no debe utilizar ninguna aplicación del 
              ecosistema TheFomLabs.
            </p>
          </section>

          {/* ===== CUENTA UNIFICADA Y SSO ===== */}
          <section id="cuenta-unificada" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              I. CUENTA UNIFICADA Y SSO (SINGLE SIGN-ON)
            </h2>

            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  A. Identidad Compartida
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Todas las aplicaciones del ecosistema TheFomLabs utilizan un <strong>sistema de 
                  autenticación centralizado (TheFomLabs Accounts)</strong>. Su perfil básico de usuario 
                  (nombre, email, contraseña) es compartido a nivel de infraestructura para facilitar 
                  el acceso único (Single Sign-On) entre aplicaciones.
                </p>
                <p className="text-gray-700 leading-relaxed mt-4">
                  <strong>Beneficios:</strong> Una sola cuenta le permite acceder a todas las 
                  aplicaciones del ecosistema sin necesidad de crear múltiples cuentas o recordar 
                  diferentes credenciales.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  B. Consentimiento Específico por App
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Aunque su identidad es compartida, sus <strong>datos privados y específicos de cada 
                  aplicación están aislados</strong>:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li><strong>Houseme:</strong> Inventarios, fotos de hogar, datos de propiedades.</li>
                  <li><strong>ThePlatform360:</strong> Clientes, inventario empresarial, órdenes, datos financieros.</li>
                  <li><strong>Traela:</strong> Envíos, rutas, datos logísticos.</li>
                  <li><strong>Sonmany:</strong> Datos específicos de la aplicación.</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  <strong>El acceso a otras aplicaciones del ecosistema requiere su activación 
                  voluntaria.</strong> Los datos no se cruzan automáticamente entre aplicaciones 
                  sin su permiso explícito.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  C. Gestión de Acceso por Dominio
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  El acceso a cada aplicación se controla mediante el sistema <strong>user_domain_map</strong>, 
                  que registra qué aplicaciones tiene activadas cada usuario. Usted puede:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Activar o desactivar acceso a aplicaciones desde su panel de cuenta.</li>
                  <li>Solicitar acceso a nuevas aplicaciones cuando estén disponibles.</li>
                  <li>Revocar acceso a aplicaciones en cualquier momento.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* ===== USO DE DATOS Y MONETIZACIÓN ===== */}
          <section id="uso-datos" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              II. USO DE DATOS Y MONETIZACIÓN
            </h2>

            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  A. Datos Recopilados
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Recopilamos los siguientes tipos de datos según la aplicación utilizada:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li><strong>Datos de Identificación:</strong> Nombre, email, teléfono, información de cuenta.</li>
                  <li><strong>Datos de Uso:</strong> Logs de acceso, acciones realizadas, preferencias.</li>
                  <li><strong>Datos de Contenido:</strong> Información ingresada por el usuario (clientes, inventario, propiedades, etc.).</li>
                  <li><strong>Datos Técnicos:</strong> IP, tipo de dispositivo, navegador, logs de errores.</li>
                  <li><strong>Datos Financieros:</strong> Información de facturación, métodos de pago (procesados por terceros seguros).</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  B. Personalización Comercial y Publicidad
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Usted autoriza a las aplicaciones del ecosistema a utilizar <strong>metadatos 
                  agregados y anonimizados</strong> de sus datos para:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Ofrecer recomendaciones personalizadas de productos y servicios.</li>
                  <li>Mostrar publicidad relevante de terceros.</li>
                  <li>Mejorar la experiencia del usuario y desarrollar nuevas funcionalidades.</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  <strong>Garantía:</strong> Los datos compartidos con anunciantes para segmentación 
                  serán siempre <strong>agregados o anonimizados</strong>. Nunca se compartirán datos 
                  personales identificables sin su consentimiento explícito.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  C. Propiedad de Datos
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Usted es el propietario exclusivo</strong> de todos los datos que ingrese, 
                  cargue o genere dentro de las aplicaciones del ecosistema. Grupo FOM no reclama 
                  propiedad sobre sus datos y se compromete a no utilizarlos para fines distintos a 
                  la prestación del servicio, salvo lo expresamente permitido en estos términos.
                </p>
              </div>
            </div>
          </section>

          {/* ===== IA Y PROPIEDAD INTELECTUAL ===== */}
          <section id="ia-propiedad" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              III. INTELIGENCIA ARTIFICIAL Y PROPIEDAD INTELECTUAL
            </h2>

            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  A. Propiedad de los Renders y Contenido Generado
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Los renders, imágenes o contenido generado por IA dentro de las aplicaciones del 
                  ecosistema son <strong>propiedad del usuario</strong> que los generó. Sin embargo:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>El <strong>prompt</strong> y los modelos subyacentes son propiedad de Grupo FOM.</li>
                  <li>No garantizamos la registrabilidad de las imágenes generadas por IA.</li>
                  <li>El usuario es responsable de verificar que el contenido generado no infrinja derechos de terceros.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  B. Entrenamiento de Modelos Propios
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Usted acepta que Grupo FOM pueda utilizar sus datos <strong>anonimizados y agregados</strong> 
                  para el entrenamiento, mejora y desarrollo de sus propios algoritmos de IA. Los datos 
                  utilizados para entrenamiento nunca incluirán información que permita identificar al 
                  usuario individual.
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-4 my-4">
                <h3 className="font-semibold text-gray-900 mb-2">C. INDEMNIDAD TOTAL (ALUCINACIONES)</h3>
                <p className="text-gray-700 leading-relaxed">
                  La IA es probabilística y puede generar resultados incorrectos o "alucinaciones". 
                  <strong> El usuario asume la total responsabilidad</strong> por decisiones estructurales, 
                  de seguridad o diseño basadas en la IA. Grupo FOM <strong>NO es responsable</strong> por 
                  daños físicos, materiales, incendios o consecuencias derivadas de errores en el análisis 
                  o recomendaciones de la IA.
                </p>
              </div>
            </div>
          </section>

          {/* ===== SEGURIDAD Y TERCEROS ===== */}
          <section id="seguridad" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              IV. SEGURIDAD, TERCEROS Y DPA
            </h2>

            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  A. Sub-procesadores
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Mantenemos <strong>Acuerdos de Procesamiento de Datos (DPAs)</strong> con los 
                  siguientes sub-procesadores:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li><strong>Supabase:</strong> Base de datos y autenticación.</li>
                  <li><strong>Google Cloud / AWS:</strong> Infraestructura y almacenamiento.</li>
                  <li><strong>OpenAI / Replicate:</strong> Servicios de IA y procesamiento de imágenes.</li>
                  <li><strong>Stripe:</strong> Procesamiento de pagos (solo datos de tarjeta, no almacenados por nosotros).</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  Todos los sub-procesadores cumplen con estándares de seguridad internacionales 
                  (SOC 2, ISO 27001) y mantienen salvaguardas adecuadas para la protección de datos.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  B. Transferencia Internacional
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Sus datos pueden ser procesados en servidores ubicados en <strong>Estados Unidos</strong> 
                  y otros países, bajo salvaguardas legales (Cláusulas Contractuales Estándar - SCCs) 
                  compatibles con la <strong>Ley 172-13 de Protección de Datos Personales</strong> de 
                  República Dominicana.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  C. Notificación de Brechas
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  En caso de una brecha de seguridad que pueda afectar sus datos personales, 
                  notificaremos:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>A los usuarios afectados en un plazo de <strong>72 horas</strong> tras detectar la brecha.</li>
                  <li>A las autoridades competentes (si es requerido por ley).</li>
                  <li>Con detalles sobre la naturaleza de la brecha, datos afectados y medidas de mitigación.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* ===== RETENCIÓN Y ASPECTOS FISCALES ===== */}
          <section id="retencion" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              V. RETENCIÓN Y ASPECTOS FISCALES
            </h2>

            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  A. Eliminación de Cuenta
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Puede eliminar su cuenta en cualquier momento desde la sección "Zona de Peligro" 
                  de cualquier aplicación o contactando a soporte. Tras la eliminación:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Sus datos se eliminan de los servidores de producción en un plazo de 30 días.</li>
                  <li>Perderá acceso a todas las aplicaciones del ecosistema.</li>
                  <li>No se otorgan reembolsos por tiempo no utilizado.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  B. Excepción Fiscal (DGII)
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>IMPORTANTE:</strong> A pesar de la eliminación de cuenta, conservaremos 
                  <strong> registros transaccionales y facturas por hasta 10 años</strong> en cumplimiento 
                  con el <strong>Código Tributario de la República Dominicana</strong> y las 
                  obligaciones ante la Dirección General de Impuestos Internos (DGII).
                </p>
                <p className="text-gray-700 leading-relaxed mt-4">
                  Estos registros se mantienen en <strong>almacenamiento frío (backups)</strong> y 
                  no son accesibles para el usuario, solo para cumplimiento regulatorio u órdenes judiciales.
                </p>
              </div>
            </div>
          </section>

          {/* ===== SECCIÓN THEPLATFORM360 (EMPRESA) ===== */}
          <section id="theplatform360" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              VI. THEPLATFORM360 (EMPRESA) - TÉRMINOS ESPECÍFICOS
            </h2>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 my-4">
              <p className="text-gray-700 leading-relaxed">
                <strong>NOTA IMPORTANTE:</strong> Esta sección contiene términos específicos para 
                ThePlatform360. Para el documento completo de términos y condiciones con las 50 
                cláusulas de blindaje, consulte: 
                <a href={getTermsUrl('empresa')} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">
                  theplatform360.app/terms
                </a>
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  A. Definición del Servicio
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  ThePlatform360 es un <strong>software de gestión empresarial (SaaS)</strong> que 
                  proporciona herramientas integradas para CRM, inventario, producción, logística, 
                  facturación, contabilidad y recursos humanos.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  B. Planes de Suscripción
                </h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li><strong>Plan Básico (Gratis):</strong> CRM limitado, Inventario básico (10 registros), 1 usuario.</li>
                  <li><strong>Plan Profesional ($120 USD/mes):</strong> Hasta 5 usuarios, inventario ilimitado, módulos de Producción.</li>
                  <li><strong>Plan Empresarial ($450 USD/mes):</strong> Usuarios ilimitados, todos los módulos, API pública.</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  <strong>Los precios NO incluyen ITBIS (18%).</strong> Periodo de prueba: 14 días gratis.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  C. Limitación de Responsabilidad (Resumen)
                </h3>
                <div className="bg-red-50 border-l-4 border-red-500 p-4 my-4">
                  <p className="text-gray-700 leading-relaxed mb-2">
                    <strong>EXONERACIÓN CRÍTICA:</strong>
                  </p>
                  <ul className="list-disc list-inside text-gray-700 space-y-1 ml-4">
                    <li>No se responde por <strong>lucro cesante</strong> o pérdidas indirectas.</li>
                    <li>Límite máximo de indemnización: <strong>lo pagado en los últimos 6 meses</strong>.</li>
                    <li>Exoneración por <strong>fuerza mayor</strong> y errores de terceros (AWS, Supabase).</li>
                    <li>El cliente <strong>indemniza a Grupo FOM</strong> por reclamos de terceros derivados de su uso.</li>
                  </ul>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  D. Módulos Específicos - Exoneraciones
                </h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li><strong>CRM:</strong> Cliente responsable de veracidad de datos de leads.</li>
                  <li><strong>Inventario:</strong> Exoneración total por descuadres físicos vs. digitales.</li>
                  <li><strong>Producción:</strong> Cliente responsable de gestión de tiempos y estados.</li>
                  <li><strong>Facturación:</strong> NO es asesor fiscal oficial (DGII). Cliente debe consultar profesionales.</li>
                  <li><strong>Integraciones (API):</strong> Cliente 100% responsable de consecuencias de conectar apps de terceros.</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  E. SLA y Soporte
                </h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li><strong>Disponibilidad garantizada:</strong> 99.5% mensual.</li>
                  <li><strong>Tiempo de respuesta soporte:</strong> 24 a 48 horas hábiles.</li>
                  <li><strong>Canales:</strong> ChatHub360, email (support@theplatform360.app).</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  F. Datos y Retención
                </h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li><strong>Propiedad:</strong> Cliente es propietario exclusivo de sus datos.</li>
                  <li><strong>Exportación:</strong> Formatos CSV y JSON disponibles en cualquier momento.</li>
                  <li><strong>Eliminación:</strong> 30 días para exportación tras cancelación, luego eliminación de producción. Backups fríos por 10 años (cumplimiento fiscal).</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  G. Pagos y Facturación
                </h3>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li><strong>Ciclos:</strong> Mensual, trimestral (5% desc.), anual (15% desc.).</li>
                  <li><strong>Métodos:</strong> Tarjeta (Stripe), transferencia bancaria, créditos prepagados.</li>
                  <li><strong>Reembolsos:</strong> NO reembolsable (salvo incumplimiento material del SLA).</li>
                  <li><strong>Periodo de gracia:</strong> 5 días calendario tras vencimiento antes de suspensión.</li>
                  <li><strong>Intereses por mora:</strong> 2% mensual (Código de Comercio RD).</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  H. Resolución de Conflictos
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Arbitraje obligatorio</strong> antes de acudir a tribunales:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li><strong>Centro:</strong> Centro de Resolución Alternativa de Controversias de la Cámara de Comercio y Producción de Santo Domingo.</li>
                  <li><strong>Árbitros:</strong> 1 (único) para agilizar y reducir costos.</li>
                  <li><strong>Idioma:</strong> Español.</li>
                  <li><strong>Jurisdicción:</strong> Tribunales del Distrito Nacional, República Dominicana.</li>
                </ul>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 my-4">
                <p className="text-gray-700 leading-relaxed">
                  <strong>⚠️ DOCUMENTO COMPLETO:</strong> Esta es una versión resumida. Para el 
                  documento completo con las 50 cláusulas de blindaje detalladas, consulte: 
                  <a href={getTermsUrl('empresa')} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">
                    theplatform360.app/terms
                  </a>
                </p>
              </div>
            </div>
          </section>

          {/* ===== SECCIÓN HOUSEME ===== */}
          <section id="houseme" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              VII. HOUSEME - TÉRMINOS ESPECÍFICOS
            </h2>

            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  A. Definición del Servicio
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Houseme es el <strong>Sistema Operativo de tu Casa</strong>, diseñado para gestionar 
                  propiedades, inventarios, espacios y timeline de eventos relacionados con el hogar.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  B. Evolución y Escalabilidad
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Grupo FOM se reserva el derecho de expandir las funcionalidades de Houseme para 
                  incluir:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Servicios de marketplace.</li>
                  <li>Conexión con proveedores técnicos.</li>
                  <li>Gestión de domótica.</li>
                  <li>Servicios financieros.</li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  <strong>El uso continuado tras la implementación de nuevas funciones constituye 
                  la aceptación automática</strong> de estos nuevos servicios bajo el marco de 
                  privacidad vigente.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  C. Marketplace y Operaciones Comerciales
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Houseme puede facilitar la contratación de servicios de terceros. Al solicitar 
                  un servicio, usted autoriza el intercambio de datos estrictamente necesarios 
                  (ubicación, fotos del área) con el proveedor del servicio.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  D. Bóveda de Activos Digitales
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  El usuario reconoce que la función de "Bóveda" busca crear un registro histórico 
                  de activos con sellado de tiempo, pero <strong>no garantiza la aceptación legal 
                  automática</strong> por parte de todas las entidades externas.
                </p>
              </div>
            </div>
          </section>

          {/* ===== SECCIÓN TRAELA ===== */}
          <section id="traela" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              VIII. TRAELA - TÉRMINOS ESPECÍFICOS
            </h2>

            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  A. Definición del Servicio
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Traela es una plataforma de gestión logística y envíos que facilita la coordinación 
                  de entregas, rutas y transporte.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  B. Responsabilidad en Envíos
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>Grupo FOM actúa únicamente como plataforma de conexión</strong> entre 
                  remitentes y transportistas. No asume responsabilidad por:
                </p>
                <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                  <li>Pérdidas, daños o retrasos en envíos.</li>
                  <li>Acciones u omisiones de transportistas de terceros.</li>
                  <li>Disputas entre remitentes y transportistas.</li>
                </ul>
              </div>
            </div>
          </section>

          {/* ===== SECCIÓN SONMANY ===== */}
          <section id="sonmany" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              IX. SONMANY - TÉRMINOS ESPECÍFICOS
            </h2>

            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  A. Definición del Servicio
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Sonmany es una aplicación del ecosistema TheFomLabs con funcionalidades específicas 
                  que se detallarán en términos adicionales cuando esté disponible públicamente.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  B. Términos Adicionales
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Los términos específicos de Sonmany se publicarán en esta sección cuando la 
                  aplicación esté disponible para usuarios generales.
                </p>
              </div>
            </div>
          </section>

          {/* ===== DISPOSICIONES FINALES ===== */}
          <section id="jurisdiccion" className="mb-12 scroll-mt-8">
            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">
              X. DISPOSICIONES FINALES
            </h2>

            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  A. Transferencia de Activos (M&A)
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  En caso de venta, fusión o adquisición de Grupo FOM, la base de datos y los 
                  activos del ecosistema se consideran <strong>activos transferibles</strong>. Los 
                  usuarios serán notificados con al menos 30 días de anticipación.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  B. Jurisdicción y Arbitraje
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Cualquier disputa se resolverá mediante <strong>arbitraje privado</strong> y en 
                  los tribunales del <strong>Distrito Nacional, República Dominicana</strong>. Las 
                  partes renuncian expresamente al derecho a un juicio por jurado.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  C. Modificaciones del Acuerdo
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Grupo FOM se reserva el derecho de modificar este Acuerdo Maestro en cualquier 
                  momento. Las modificaciones entrarán en vigor 30 días después de la publicación, 
                  con notificación por email. El uso continuado constituye aceptación de los nuevos términos.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">
                  D. Acuerdo Completo
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Este Acuerdo Maestro, junto con los términos específicos de cada aplicación, 
                  constituye el <strong>acuerdo completo</strong> entre el usuario y Grupo FOM. 
                  Este documento anula cualquier acuerdo previo relacionado con el ecosistema.
                </p>
              </div>
            </div>
          </section>

          {/* ===== CONTACTO ===== */}
          <section id="contacto" className="mb-12 mt-12 scroll-mt-8">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4">Contacto Legal</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Para consultas legales, notificaciones o ejercer sus derechos, puede contactarnos en:
              </p>
              <ul className="text-gray-700 space-y-2">
                <li><strong>Email Legal:</strong> <a href="mailto:legal@thefomlabs.com" className="text-blue-600 hover:underline">legal@thefomlabs.com</a></li>
                <li><strong>DPO (Data Protection Officer):</strong> <a href="mailto:dpo@thefomlabs.com" className="text-blue-600 hover:underline">dpo@thefomlabs.com</a></li>
                <li><strong>Privacidad:</strong> <a href="mailto:privacy@thefomlabs.com" className="text-blue-600 hover:underline">privacy@thefomlabs.com</a></li>
                <li><strong>General:</strong> <a href="mailto:contacto@thefomlabs.com" className="text-blue-600 hover:underline">contacto@thefomlabs.com</a></li>
              </ul>
            </div>
          </section>
        </article>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-gray-50 mt-16">
        <div className="container mx-auto max-w-4xl px-4 py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Información de contacto */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Grupo FOM, S.R.L.</h3>
              <p className="text-sm text-gray-600 mb-2">
                <strong>Domicilio Legal:</strong><br />
                B Esquina 1ra No. 1<br />
                Santo Domingo, República Dominicana
              </p>
              <p className="text-sm text-gray-600 mb-2">
                <strong>RNC:</strong> 133308355
              </p>
            </div>

            {/* Contacto */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Contacto</h3>
              <p className="text-sm text-gray-600 mb-2">
                <strong>Legal:</strong><br />
                <a href="mailto:legal@thefomlabs.com" className="text-blue-600 hover:text-blue-800 underline">
                  legal@thefomlabs.com
                </a>
              </p>
              <p className="text-sm text-gray-600 mb-2">
                <strong>DPO:</strong><br />
                <a href="mailto:dpo@thefomlabs.com" className="text-blue-600 hover:text-blue-800 underline">
                  dpo@thefomlabs.com
                </a>
              </p>
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center">
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} Grupo FOM, S.R.L. Todos los derechos reservados.
            </p>
            <p className="text-xs text-gray-400 mt-2">
              Versión v1.0 (Edición Ecosistema Unificado) - Vigente desde el 1 de febrero de 2026
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MasterTermsPage;
